import { Store, select } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { State } from '@app/store/state/state';
import {
  TravelerPaxResponse, TravelerPaxIdentificator,
  TravelerPax, TravelerPaxResponseVm, Loyalty, LoyaltyResponse
} from '@pax/entities/traveler-pax';
import { selectTravelerPaxState } from '@pax/store/selectors/travelers.selector';
import { selectPassengerListUI } from '@pax/store/selectors/passenger-list-ui.selector';
import { TravelerItemUI, TravelerIdentifier, TravelerRoomUI } from '@pax/entities/pax-ui';
import * as actions from '@pax/store/actions/pax.actions';
import { selectGuestListUI } from '@pax/store/selectors/guest-list-ui.selector';
import { paxRootSelector } from '@pax/store/selectors/pax-root.selector';
import { PaxState } from '@pax/store/state/pax.state';
import { selectPaxLoading } from '@pax/store/selectors/pax-loading-form.selector';
import { AirValidationResponse } from '@app/entities/air-validation.response';
import { bookingRootSelector } from '@booking/store/selectors/booking-root.selector';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { UserGender } from '@booking/entities/user-gender';
import { User } from '@app/modules/core/interfaces/user';
import { VehicleValidateResponse } from '@autos/entities/vehicle-validate.response';
import { LoyaltyProgramResponse, MarriottEnrollment, MarriottEnrollmentResponse } from '@pax/entities/loyalty-program.response';
import { MarriottCreateFormUIResponse } from '@pax/entities/marriott-create-form';
import { selectHaveBeenLoaded } from '@pax/store/selectors/pax-have-been-loaded.selector';

@Injectable()
export class PaxFacade {

  constructor(private store: Store<State>) { }

  public travelers$: Observable<TravelerPaxResponse[]> = this.store
    .pipe(select(selectTravelerPaxState));

  public travelersHaveBeenLoaded$: Observable<boolean> = this.store
    .pipe(select(selectHaveBeenLoaded));

  public passengerListUI$: Observable<TravelerItemUI[]> = this.store
    .pipe(select(selectPassengerListUI));

  public guestListUI$: Observable<TravelerRoomUI[]> = this.store
    .pipe(select(selectGuestListUI));

  public isLoadingTravelers$: Observable<boolean> = this.store.pipe(
    select(paxRootSelector),
    map((state: PaxState) => state.ui.isLoadingTravelers)
  );
  public isLoadingTravelersForm$: Observable<boolean> = this.store.pipe(select(selectPaxLoading));

  public isCreateTraveler$: Observable<boolean> = this.store.pipe(
    select(paxRootSelector),
    map((state: PaxState) => state.ui.isCreateTraveler)
  );

  public bookingType$: Observable<TravelBookingType> = this.store.pipe(
    select(paxRootSelector),
    map((state: PaxState) => state.ui.bookingType)
  );

  public isLoadingMarriottCreateAccount$: Observable<boolean> = this.store.pipe(
    select(paxRootSelector),
    map((state: PaxState) => state.ui.isLoadingMarriottCreateAccount)
  );

  public airValidation$: Observable<AirValidationResponse> = this.store.pipe(
    select(bookingRootSelector),
    map(state => state.airValidation)
  );

  public hotelValidation$: Observable<HotelValidationResponse> = this.store.pipe(
    select(bookingRootSelector),
    map(state => state.hotelValidation)
  );

  public celebratePaxView$: Observable<UserGender> = this.store.pipe(
    select(paxRootSelector),
    map((state: PaxState) => state.ui.celebratePaxView)
  );

  public marriottCreateAccountShowStatus$: Observable<MarriottCreateFormUIResponse> = this.store.pipe(
    select(paxRootSelector),
    map((state: PaxState) => state.ui.marriottCreateAccountShowStatus)
  );

  public marriottloyaltyNumber$: Observable<LoyaltyProgramResponse> = this.store.pipe(
    select(paxRootSelector),
    map((state: PaxState) => state.marriottloyaltyNumber)
  );

  public marriottCreatedAccount$: Observable<MarriottEnrollmentResponse> = this.store.pipe(
    select(paxRootSelector),
    map((state: PaxState) => state.marriottCreatedAccount)
  );

  public autoValidation$: Observable<VehicleValidateResponse> = this.store.pipe(
    select(bookingRootSelector),
    map(state => state.autoValidation)
  );

  public activeBonvoyNumber$: Observable<boolean> = this.store.pipe(
    select(paxRootSelector),
    map((state: PaxState) => state?.ui?.activeBonvoyNumber)
  );

  public resetActiveBonvoyNumber(): void {
    this.store.dispatch(actions.resetActiveBonvoyNumberAction())
  }

  public updatePax(payload: TravelerPaxResponseVm, identifier: TravelerIdentifier): void {
    this.store.dispatch(actions.updatePaxAction({ payload, identifier }));
  }

  public deletePax(payload: TravelerPaxIdentificator, id: number, guestId?: number): void {
    this.store.dispatch(actions.deletePaxAction({ payload, id, guestId }));
  }

  public createPax(payload: TravelerPax, identifier: TravelerIdentifier, celebrateAfterCreatedPax: boolean): void {
    this.store.dispatch(actions.createPaxAction({ payload, identifier, celebrateAfterCreatedPax }));
  }

  public setLoadingTravelerForm(isLoadingTravelerForm: boolean): void {
    this.store.dispatch(actions.setLoadingTravelerForm({ isLoadingTravelerForm }));
  }

  public onlyAddToList(payload: TravelerPax, identifier: TravelerIdentifier): void {
    this.store.dispatch(actions.onlyAddToListAction({ payload, identifier }));
  }

  public onlyUpdateToList(payload: TravelerPaxResponseVm, identifier: TravelerIdentifier): void {
    this.store.dispatch(actions.onlyUpdateToListAction({ payload, identifier }));
  }

  public onlyDeleteToList(payload: TravelerPaxIdentificator, id: number, guestId?: number): void {
    this.store.dispatch(actions.onlyDeleteToListAction({ payload, id, guestId }));
  }

  public setTavelerPax(
    payload: TravelerPaxResponse,
    id: number,
    list: TravelerPaxResponse[] = [],
    defaultAirline?: string,
    guestId?: number
  ): void {
    this.store.dispatch(actions.setTavelerPaxAction({
      config: { payload, id, guestId, list, defaultAirline }
    }));
  }

  public addLoyaltyProgram(payload: Loyalty, id: number): void {
    this.store.dispatch(actions.addLoyaltyProgramAction({ payload, id }));
  }

  public createLoyaltyProgram(payload: Loyalty, id: number): void {
    this.store.dispatch(actions.createLoyaltyProgramAction({ payload, id }));
  }

  public updateLoyaltyProgram(payload: LoyaltyResponse, id: number): void {
    this.store.dispatch(actions.updateLoyaltyProgramAction({ payload, id }));
  }

  public deleteLoyaltyProgram(payload: LoyaltyResponse, id: number): void {
    this.store.dispatch(actions.deleteLoyaltyProgramAction({ payload, id }));
  }

  public setLoyaltyProgram(payload: LoyaltyResponse, id: number): void {
    this.store.dispatch(actions.setLoyaltyProgramAction({ payload, id }));
  }

  public setCreatePAXUI(payload: boolean): void {
    this.store.dispatch(actions.setCreatePAXUIAction({ payload }));
  }

  public removeDefaultPax(id: number): void {
    this.store.dispatch(actions.removeDefaultPaxAction({ id }));
  }

  public removeDefaultPaxByGuestRoomAction(roomIndex: number, guestId: number): void {
    this.store.dispatch(actions.removeDefaultPaxByGuestRoomAction({ roomIndex, guestId }));
  }

  public addPackageLoyaltyProgram(payload: Loyalty, id: number, guestId: number): void {
    this.store.dispatch(actions.addPackageLoyaltyProgramAction({ payload, id, guestId }));
  }

  public createPackageLoyaltyProgram(payload: Loyalty, id: number, guestId: number): void {
    this.store.dispatch(actions.createPackageLoyaltyProgramAction({ payload, id, guestId }));
  }

  public updatePacakgeLoyaltyProgram(payload: LoyaltyResponse, id: number, guestId: number): void {
    this.store.dispatch(actions.updatePackageLoyaltyProgramAction({ payload, id, guestId }));
  }

  public deletePacakgeLoyaltyProgram(payload: LoyaltyResponse, id: number, guestId: number): void {
    this.store.dispatch(actions.deletePackageLoyaltyProgramAction({ payload, id, guestId }));
  }

  public setPacakgeLoyaltyProgram(payload: LoyaltyResponse, id: number, guestId: number): void {
    this.store.dispatch(actions.setPackageLoyaltyProgramAction({ payload, id, guestId }));
  }

  public setIsActiveCelebratePaxView(payload: UserGender): void {
    this.store.dispatch(actions.setIsActiveCelebratePaxViewAction({ payload }));
  }

  public configDefaultByInfoType(
    travelers: TravelerPaxResponse[], user: User, type: TravelBookingType
  ): void {
    this.store.dispatch(actions.configDefaultByInfoTypeAction({
      config: { travelers, user, bookType: type }
    }));
  }

  public fetchMarriottLoyaltyNumber(payload: number): void {
    this.store.dispatch(actions.fetchMarriottLoyaltyNumber({ payload }))
  }

  public createMarriottBonvoyAccount(payload: MarriottEnrollment): void {
    this.store.dispatch(actions.createMarriottBonvoyAccount({ payload }))
  }

  public resetMarriottCreateAccountShowStatus(): void {
    this.store.dispatch(actions.resetMarriottCreateAccountShowStatus())
  }
}
